import { useState, useEffect, useRef, useCallback } from 'react';
import { MessageCircle, Edit2, Trash2, AlertCircle, Heart, Pin, MoreVertical, Flag, UserX } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { db } from '../../lib/firebase';
import { doc, getDoc, updateDoc, onSnapshot, deleteDoc, increment, arrayUnion, arrayRemove, serverTimestamp, collection, addDoc, setDoc } from 'firebase/firestore';
import { formatDistanceToNow } from 'date-fns';
import ReactPlayer from 'react-player';
import CommentModal from '../modals/CommentModal';
import DeleteModal from '../modals/DeleteModal';
import EditPostModal from '../modals/EditPostModal';
import MediaPreview from './MediaPreview';
import LinkPreview from './LinkPreview';
import TikTokPreview from './TikTokPreview';
import ArticlePreview from './ArticlePreview';
import { vibrate, HapticPatterns } from '../../utils/haptics';

interface Post {
  id: string;
  content: string;
  authorId: string;
  authorName: string;
  authorPhoto?: string;
  mediaUrls: string[];
  mediaTypes: string[];
  likes: number;
  comments: number;
  shares: number;
  likedBy: string[];
  createdAt: any;
  reactions?: { [key: string]: { count: number; userIds: string[] } };
  isPinned?: boolean;
  inPinnedSection?: boolean;
}

interface PostItemProps {
  post: Post;
  onProfileClick: (userId: string) => void;
  highlightedCommentId?: string | null;
  onPostDeleted: (postId: string) => void;
}

interface Comment {
  id: string;
  content: string;
  authorId: string;
  authorName: string;
  authorPhoto?: string;
  replies: string[];
  createdAt: any;
}

interface CommentItemProps {
  comment: Comment;
  onReplyClick: (commentId: string) => void;
  highlightedCommentId?: string | null;
  onProfileClick: (userId: string) => void;
}

interface ReplyItemProps {
  reply: Comment;
  onProfileClick: (userId: string) => void;
}

const REACTIONS = [
  { id: 'like', emoji: '❤️', label: 'Heart' },
  { id: 'laugh', emoji: '😂', label: 'Laugh' },
  { id: 'fire', emoji: '🔥', label: 'Fire' },
  { id: 'clap', emoji: '👏', label: 'Clap' },
  { id: 'thinking', emoji: '🤔', label: 'Thinking' }
];

export default function PostItem({ post, onProfileClick, highlightedCommentId, onPostDeleted }: PostItemProps) {
  const { user } = useAuth();

  // Check if post author is blocked
  const blockedUsers = JSON.parse(localStorage.getItem('blockedUsers') || '[]');
  if (blockedUsers.includes(post.authorId)) {
    return null; // Don't render posts from blocked users
  }
  const [postState, setPostState] = useState(post);
  const [showOptionsMenu, setShowOptionsMenu] = useState(false);
  const optionsMenuRef = useRef<HTMLDivElement>(null);
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(post.content);
  const [isExpanded, setIsExpanded] = useState(false);
  const [authorProfilePic, setAuthorProfilePic] = useState<string>(post.authorPhoto || '');
  const [videoError, setVideoError] = useState<string | null>(null);
  const videoRef = useRef<HTMLDivElement>(null);
  const [isVideoVisible, setIsVideoVisible] = useState(false);
  const [showReactionPicker, setShowReactionPicker] = useState(false);
  const [longPressTimer, setLongPressTimer] = useState<NodeJS.Timeout | null>(null);
  const reactionPickerRef = useRef<HTMLDivElement>(null);

  // Handle clicking outside of options menu
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (optionsMenuRef.current && !optionsMenuRef.current.contains(event.target as Node)) {
        setShowOptionsMenu(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleReaction = async (reactionType: string) => {
    if (!user) return;

    try {
      const postRef = doc(db, 'posts', post.id);
      const postDoc = await getDoc(postRef);

      if (!postDoc.exists()) return;

      const currentReactions = postDoc.data()?.reactions || {};

      // Remove user from all other reactions first
      const updates: any = {};
      REACTIONS.forEach(reaction => {
        const reactionData = currentReactions[reaction.id] || { count: 0, userIds: [] };
        const hasThisReaction = reactionData.userIds?.includes(user.uid);

        if (reaction.id === reactionType) {
          // Toggle the selected reaction
          updates[`reactions.${reaction.id}`] = {
            count: hasThisReaction ? Math.max(0, reactionData.count - 1) : reactionData.count + 1,
            userIds: hasThisReaction
              ? reactionData.userIds.filter((id: string) => id !== user.uid)
              : [...reactionData.userIds, user.uid]
          };
        } else if (hasThisReaction) {
          // Remove from other reactions
          updates[`reactions.${reaction.id}`] = {
            count: Math.max(0, reactionData.count - 1),
            userIds: reactionData.userIds.filter((id: string) => id !== user.uid)
          };
        }
      });

      await updateDoc(postRef, updates);

      // Vibrate feedback
      vibrate(HapticPatterns.MEDIUM);

      // Close picker
      setShowReactionPicker(false);

    } catch (err) {
      console.error('Error handling reaction:', err);
    }
  };

  // Get user's current reaction
  const getUserReaction = () => {
    if (!user) return null;
    for (const reaction of REACTIONS) {
      if (postState.reactions?.[reaction.id]?.userIds?.includes(user.uid)) {
        return reaction;
      }
    }
    return null;
  };

  // Get total reaction count
  const getTotalReactionCount = () => {
    if (!postState.reactions) return 0;
    return REACTIONS.reduce((total, reaction) => {
      return total + (postState.reactions?.[reaction.id]?.count || 0);
    }, 0);
  };

  // Handle long press for mobile
  const handleTouchStart = () => {
    const timer = setTimeout(() => {
      setShowReactionPicker(true);
      vibrate(HapticPatterns.LIGHT);
    }, 500); // 500ms long press
    setLongPressTimer(timer);
  };

  const handleTouchEnd = () => {
    if (longPressTimer) {
      clearTimeout(longPressTimer);
      setLongPressTimer(null);
    }
  };

  // Quick reaction (tap/click on main button)
  const handleQuickReaction = () => {
    const userReaction = getUserReaction();
    if (userReaction) {
      // User already reacted, remove their reaction
      handleReaction(userReaction.id);
    } else {
      // Default to "like" for quick reaction
      handleReaction('like');
    }
  };

  // Close reaction picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (reactionPickerRef.current && !reactionPickerRef.current.contains(event.target as Node)) {
        setShowReactionPicker(false);
      }
    };

    if (showReactionPicker) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showReactionPicker]);

  // Fetch current profile picture
  useEffect(() => {
    const fetchProfilePic = async () => {
      try {
        const userDoc = await getDoc(doc(db, 'users', post.authorId));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          setAuthorProfilePic(userData.photoURL || '');
        }
      } catch (error) {
        console.error('Error fetching user profile picture:', error);
      }
    };

    fetchProfilePic();
  }, [post.authorId]);

  // Close reaction picker when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as HTMLElement;
      if (!target.closest('.reaction-picker')) {
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Open comment modal if highlightedCommentId is present
  useEffect(() => {
    if (highlightedCommentId) {
      setShowCommentModal(true);
    }
  }, [highlightedCommentId]);

  // Intersection Observer for lazy loading videos
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVideoVisible(true);
          } else {
            setIsVideoVisible(false);
          }
        });
      },
      { threshold: 0.1 }
    );

    if (videoRef.current) {
      observer.observe(videoRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Subscribe to post updates
  useEffect(() => {
    const postRef = doc(db, 'posts', post.id);
    const unsubscribe = onSnapshot(postRef, (doc) => {
      if (doc.exists()) {
        const data = doc.data();
        setPostState(prev => ({
          ...prev,
          likes: data.likes || 0,
          likedBy: data.likedBy || [],
          reactions: data.reactions || {},
          comments: data.comments || prev.comments
        }));
      }
    });

    return () => unsubscribe();
  }, [post.id]);

  const handleLike = async () => {
    if (!user) return;

    try {
      const postRef = doc(db, 'posts', post.id);
      const postDoc = await getDoc(postRef);
      
      if (!postDoc.exists()) return;

      const currentData = postDoc.data();
      const isLiked = currentData.likedBy?.includes(user.uid);

      // Only update Firestore and let the snapshot listener handle the UI
      await updateDoc(postRef, {
        likedBy: isLiked ? arrayRemove(user.uid) : arrayUnion(user.uid),
        likes: (isLiked ? currentData.likedBy.length - 1 : currentData.likedBy.length + 1)
      });

      // Create notification
      if (user.uid !== post.authorId) {
        console.log('Creating like notification...');
        // Get user data for the notification
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        const userData = userDoc.exists() ? userDoc.data() : null;
        const username = userData?.username || user.email?.split('@')[0] || 'Anonymous';

        // Create notification
        const notificationRef = doc(collection(db, 'notifications'));
        await setDoc(notificationRef, {
          type: 'like',
          postId: post.id,
          fromUserId: user.uid,
          fromUserName: username,
          toUserId: post.authorId,
          read: false,
          createdAt: serverTimestamp(),
          postContent: post.content.substring(0, 100) // Add a preview of the post content
        });
      } else {
        console.log('No notification created - user liked their own post');
      }
    } catch (err) {
      console.error('Error toggling like:', err);
    }
  };

  const handleUpdatePost = async () => {
    if (!user || !editContent.trim()) return;

    try {
      await updateDoc(doc(db, 'posts', post.id), {
        content: editContent.trim()
      });
      setIsEditing(false);
    } catch (err) {
      console.error('Error updating post:', err);
    }
  };

  const handleDeletePost = async () => {
    if (!user) return;

    try {
      await deleteDoc(doc(db, 'posts', post.id));
      onPostDeleted(post.id);
      setShowDeleteModal(false);
    } catch (err) {
      console.error('Error deleting post:', err);
    }
  };

  const handleBlockUser = () => {
    if (!user) {
      alert('Please log in to block users');
      return;
    }

    // Store blocked users in localStorage
    const blockedUsers = JSON.parse(localStorage.getItem('blockedUsers') || '[]');
    if (!blockedUsers.includes(post.authorId)) {
      blockedUsers.push(post.authorId);
      localStorage.setItem('blockedUsers', JSON.stringify(blockedUsers));
      setShowOptionsMenu(false);
      alert(`You have blocked ${post.authorName}`);
    } else {
      alert(`You have already blocked ${post.authorName}`);
    }
  };

  const handleUnblockUser = () => {
    if (!user) {
      alert('Please log in to unblock users');
      return;
    }

    const blockedUsers = JSON.parse(localStorage.getItem('blockedUsers') || '[]');
    if (blockedUsers.includes(post.authorId)) {
      const updatedBlockedUsers = blockedUsers.filter(id => id !== post.authorId);
      localStorage.setItem('blockedUsers', JSON.stringify(updatedBlockedUsers));
      setShowOptionsMenu(false);
      alert(`You have unblocked ${post.authorName}`);
      window.location.reload(); // Refresh to show previously blocked posts
    } else {
      alert(`${post.authorName} is not blocked`);
    }
  };

  const handleReportPost = () => {
    if (!user) {
      alert('Please log in to report posts');
      return;
    }

    // Store reported post in localStorage
    const reports = JSON.parse(localStorage.getItem('reportedPosts') || '[]');
    reports.push({
      id: Math.random().toString(36).substring(7), // Simple unique ID
      postId: post.id,
      reportedBy: user.uid,
      reportedUser: post.authorId,
      content: post.content,
      status: 'pending',
      createdAt: new Date().toISOString()
    });
    localStorage.setItem('reportedPosts', JSON.stringify(reports));
    
    setShowOptionsMenu(false);
    alert('Thank you for reporting this post. Our team will review it shortly.');
  };

  const CHARACTER_LIMIT = 150;
  const shouldTruncate = (post.content?.length || 0) > CHARACTER_LIMIT;

  const contentClass = (post.content?.length || 0) < 120
    ? "text-2xl sm:text-3xl font-bold leading-tight text-gray-900 dark:text-white tracking-tight"
    : "text-[16px] sm:text-[18px] leading-relaxed text-gray-800 dark:text-gray-100 font-medium";

  const renderContent = () => {
    if (isEditing) {
      return (
        <textarea
          value={editContent}
          onChange={(e) => setEditContent(e.target.value)}
          className="w-full bg-white border border-gray-300 rounded-lg px-4 py-2 text-gray-800 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
          rows={3}
        />
      );
    }

    // Handle URLs and embeds
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const parts = (post.content || '').split(urlRegex);
    const urls = (post.content || '').match(urlRegex) || [];
    let urlIndex = 0;

    return (
      <div className={`whitespace-pre-wrap break-words ${contentClass}`}>
        {parts.map((part, i) => {
          if (i % 2 === 0) {
            // Text content
            if (!isExpanded && shouldTruncate && i === 0) {
              return part.slice(0, CHARACTER_LIMIT) + "...";
            }
            return part;
          } else {
            // URL content
            const url = urls[urlIndex++];
            if (url.includes('youtube.com') || url.includes('youtu.be')) {
              const videoId = getYouTubeVideoId(url);
              if (videoId) {
                return (
                  <div key={i} className="my-4 aspect-video w-full max-w-[600px]">
                    <iframe
                      width="100%"
                      height="100%"
                      src={`https://www.youtube.com/embed/${videoId}`}
                      title="YouTube video player"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      className="rounded-lg"
                    ></iframe>
                  </div>
                );
              }
            } else if (url.includes('facebook.com')) {
              return <LinkPreview key={i} url={url} />;
            } else if (url.includes('tiktok.com')) {
              return <TikTokPreview key={i} url={url} />;
            } else {
              return <ArticlePreview key={i} url={url} />;
            }
          }
        })}
      </div>
    );
  };

  const handleVideoError = useCallback(() => {
    setVideoError('This video might be too large to play. Try downloading it instead.');
  }, []);

  const fetchReactionUsers = async (userIds: string[]) => {
    const users = [];
    for (const userId of userIds) {
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        users.push({
          id: userId,
          name: userData.username || 'Anonymous',
          photo: userData.photoURL
        });
      }
    }
    return users;
  };

  // Function to extract YouTube video ID from URL
  const getYouTubeVideoId = (url: string): string | null => {
    const regExp = /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[7].length === 11) ? match[7] : null;
  };

  const handleCommentAdded = useCallback(() => {
    // Update post state optimistically
    setPostState(prev => ({
      ...prev,
      comments: (prev.comments || 0) + 1
    }));
  }, []);

  const handleCommentDeleted = useCallback((totalDeleted: number) => {
    // Update post state optimistically
    setPostState(prev => ({
      ...prev,
      comments: Math.max(0, (prev.comments || 0) - totalDeleted)
    }));
  }, []);

  return (
    <article
      id={`post-${post.id}`}
      className={`relative bg-white dark:bg-navy-800 rounded-[32px] p-6 sm:p-8 shadow-[0_2px_20px_rgb(0,0,0,0.04)] dark:shadow-none border border-gray-100 dark:border-navy-700 transition-all duration-300 hover:shadow-[0_8px_30px_rgb(0,0,0,0.08)] ${post.isPinned && !post.inPinnedSection ? 'ring-2 ring-orange-500' : ''}`}
    >
      {post.isPinned && !post.inPinnedSection && (
        <div className="absolute -top-3 left-6 bg-gradient-to-r from-orange-400 to-orange-500 text-white text-[10px] font-bold uppercase tracking-widest px-3 py-1 rounded-full flex items-center shadow-md">
          <Pin size={12} className="mr-1" />
          Pinned
        </div>
      )}

      {/* Author Header */}
      <div className="flex justify-between items-center mb-6">
        <button onClick={() => onProfileClick(post.authorId)} className="flex items-center gap-3 group/author outline-none">
          <div className="relative flex-shrink-0">
            {authorProfilePic ? (
              <img src={authorProfilePic} alt={post.authorName} className="w-12 h-12 min-w-[3rem] min-h-[3rem] flex-shrink-0 rounded-full object-cover ring-2 ring-white dark:ring-navy-800 shadow-sm group-hover/author:ring-orange-200 transition-all" />
            ) : (
              <div className="avatar-48 w-12 h-12 min-w-[3rem] min-h-[3rem] flex-shrink-0 rounded-full bg-gradient-to-tr from-orange-400 to-orange-500 flex items-center justify-center text-white font-bold text-lg ring-2 ring-white dark:ring-navy-800 shadow-sm group-hover/author:ring-orange-200 transition-all">
                {post.authorName[0].toUpperCase()}
              </div>
            )}
          </div>
          <div className="flex flex-col text-left">
            <span className="font-bold text-[16px] text-gray-900 dark:text-white group-hover/author:text-orange-500 transition-colors capitalize tracking-tight flex items-center gap-2">
              {post.authorName}
              {user?.email === 'manascojohnm@gmail.com' && post.authorId === user?.uid && (
                <span className="bg-orange-100 dark:bg-orange-500/20 text-orange-600 dark:text-orange-400 text-[10px] font-bold px-2 py-0.5 rounded-md uppercase tracking-wider">
                  Admin
                </span>
              )}
            </span>
            <span className="text-[13px] font-medium text-gray-500 dark:text-gray-400">
              {post.createdAt?.seconds ? formatDistanceToNow(new Date(post.createdAt.seconds * 1000), { addSuffix: true }) : 'Just now'}
            </span>
          </div>
        </button>

        {/* Header Actions */}
        <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity md:opacity-100">
          {(user?.uid === post.authorId || user?.email === 'manascojohnm@gmail.com') && (
            <div className="flex gap-1 mr-1">
              {user?.uid === post.authorId && (
                <button onClick={() => setIsEditing(true)} className="p-2 text-gray-400 hover:text-orange-500 hover:bg-orange-50 dark:hover:bg-orange-500/10 rounded-full transition-colors" title="Edit post">
                  <Edit2 size={16} />
                </button>
              )}
              <button onClick={() => setShowDeleteModal(true)} className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-full transition-colors" title="Delete post">
                <Trash2 size={16} />
              </button>
            </div>
          )}

          {/* Options Menu (three dots) */}
          <div className="relative">
            <button onClick={() => setShowOptionsMenu(!showOptionsMenu)} className="p-2 text-gray-400 hover:text-gray-900 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-navy-700 rounded-full transition-colors">
              <MoreVertical size={18} />
            </button>

            {showOptionsMenu && (
              <div ref={optionsMenuRef} className="absolute right-0 mt-2 w-48 bg-white dark:bg-navy-800 rounded-xl shadow-[0_8px_30px_rgb(0,0,0,0.12)] dark:shadow-none border border-gray-100 dark:border-navy-700 py-1.5 z-10">
                <button onClick={handleReportPost} className="flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-navy-700 w-full text-left">
                  <Flag className="mr-3 h-4 w-4 text-gray-400" />
                  Report Post
                </button>
                {user && !JSON.parse(localStorage.getItem('blockedUsers') || '[]').includes(post.authorId) && (
                  <button onClick={handleBlockUser} className="flex items-center px-4 py-2 text-sm text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 w-full text-left">
                    <UserX className="mr-3 h-4 w-4" />
                    Block User
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="mb-6">
        {isEditing ? (
          <div className="space-y-3">
            <textarea
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              className="w-full bg-gray-50 dark:bg-navy-900 border border-gray-200 dark:border-navy-700 rounded-2xl px-5 py-4 text-gray-800 dark:text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500/50 resize-none text-[16px]"
              rows={4}
            />
            <div className="flex justify-end gap-2">
              <button onClick={() => setIsEditing(false)} className="px-5 py-2.5 text-sm font-bold text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors rounded-xl hover:bg-gray-100 dark:hover:bg-navy-700">
                Cancel
              </button>
              <button onClick={handleUpdatePost} className="px-6 py-2.5 text-sm font-bold bg-orange-500 text-white rounded-xl hover:bg-orange-600 transition-all shadow-md hover:shadow-lg active:scale-95">
                Save
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            <div>
              {renderContent()}
            </div>
            {shouldTruncate && (
              <button onClick={() => setIsExpanded(!isExpanded)} className="text-orange-500 hover:text-orange-600 text-sm font-bold transition-colors mt-2">
                {isExpanded ? 'Show less' : 'Read more'}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Media section */}
      {post.mediaUrls && post.mediaUrls.length > 0 && (
        <div className="mb-8 overflow-hidden rounded-[24px] bg-gray-50 dark:bg-navy-900 border border-gray-100 dark:border-navy-700">
          {post.mediaUrls.map((url, index) => (
            <div key={index} className="w-full">
              {post.mediaTypes[index] === 'video' ? (
                <div ref={videoRef} className="relative aspect-video w-full">
                  {isVideoVisible ? (
                    <>
                      <ReactPlayer
                        url={url}
                        controls
                        width="100%"
                        height="100%"
                        className="!max-h-[500px] mx-auto"
                        config={{
                          file: {
                            attributes: { controlsList: 'nodownload', preload: 'metadata' }
                          }
                        }}
                        onError={handleVideoError}
                        playsinline
                        pip={false}
                      />
                      {videoError && (
                        <div className="p-3 text-sm text-red-500 flex items-center justify-center gap-2 bg-red-50 dark:bg-red-900/20">
                          <AlertCircle size={16} />
                          <p>{videoError}</p>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gray-100 dark:bg-navy-800">
                      <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Loading video...</span>
                    </div>
                  )}
                </div>
              ) : (
                <img
                  src={url}
                  alt={`Post media ${index + 1}`}
                  className="w-full max-h-[500px] object-cover mx-auto"
                  loading="lazy"
                />
              )}
            </div>
          ))}
        </div>
      )}

      {/* Modern Interaction Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mt-2 pt-4 border-t border-gray-100 dark:border-navy-700/60">
        {/* Reactions List */}
        <div className="flex items-center gap-2 flex-wrap">
          {getTotalReactionCount() > 0 ? (
            <div className="flex gap-1.5 mr-2">
              {REACTIONS.map((reaction) => {
                const count = postState.reactions?.[reaction.id]?.count || 0;
                const hasReacted = postState.reactions?.[reaction.id]?.userIds?.includes(user?.uid);
                if (count === 0) return null;
                return (
                  <button
                    key={reaction.id}
                    onClick={() => handleReaction(reaction.id)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-bold transition-all ${
                      hasReacted
                        ? 'bg-orange-50 dark:bg-orange-500/20 text-orange-600 dark:text-orange-400 border border-orange-200 dark:border-orange-500/30'
                        : 'bg-gray-50 dark:bg-navy-900 text-gray-600 dark:text-gray-300 border border-gray-100 dark:border-navy-700 hover:bg-gray-100 dark:hover:bg-navy-700'
                    }`}
                  >
                    <span>{reaction.emoji}</span>
                    <span>{count}</span>
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="text-sm text-gray-400 dark:text-gray-500 italic">No reactions yet</div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-2">
          {/* Reaction Picker Trigger */}
          <div
            className="relative"
            ref={reactionPickerRef}
            onMouseEnter={() => setShowReactionPicker(true)}
            onMouseLeave={() => setShowReactionPicker(false)}
          >
            <button
              onClick={handleQuickReaction}
              onTouchStart={handleTouchStart}
              onTouchEnd={handleTouchEnd}
              className={`group flex items-center justify-center w-10 h-10 rounded-full transition-all duration-200 ${
                getUserReaction()
                  ? 'bg-orange-50 dark:bg-orange-500/20 text-orange-600 dark:text-orange-400'
                  : 'bg-gray-50 dark:bg-navy-900 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-navy-700'
              }`}
            >
              {getUserReaction() ? (
                <span className="text-lg transform group-hover:scale-110 transition-transform">{getUserReaction()?.emoji}</span>
              ) : (
                <Heart className="w-5 h-5 stroke-[2] transform group-hover:scale-110 transition-transform" />
              )}
            </button>

            {/* Reaction Picker Popover */}
            {showReactionPicker && (
              <>
                <div className="absolute bottom-full right-0 sm:left-1/2 sm:-translate-x-1/2 h-4 z-40" />
                <div
                  className="absolute bottom-full right-0 sm:left-1/2 sm:-translate-x-1/2 mb-2 bg-white dark:bg-navy-800 rounded-full shadow-[0_8px_30px_rgb(0,0,0,0.15)] border border-gray-100 dark:border-navy-700 px-3 py-2 flex gap-2 z-50 transform origin-bottom animate-in fade-in slide-in-from-bottom-2 duration-200"
                  onMouseEnter={() => setShowReactionPicker(true)}
                >
                {REACTIONS.map((reaction) => {
                  const hasReacted = postState.reactions?.[reaction.id]?.userIds?.includes(user?.uid);
                  return (
                    <button
                      key={reaction.id}
                      onClick={() => handleReaction(reaction.id)}
                      className={`relative group p-2 rounded-full transition-all duration-200 hover:-translate-y-2 hover:scale-125 ${
                        hasReacted ? 'bg-orange-50 dark:bg-orange-500/20' : 'hover:bg-gray-50 dark:hover:bg-navy-700'
                      }`}
                      title={reaction.label}
                    >
                      <span className="text-3xl drop-shadow-sm leading-none block">{reaction.emoji}</span>
                    </button>
                  );
                })}
              </div>
              </>
            )}
          </div>

          <button
            onClick={() => setShowCommentModal(true)}
            className="group flex items-center gap-2 px-4 py-2 bg-gray-50 dark:bg-navy-900 rounded-full text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-navy-700 transition-all duration-200 font-bold text-sm"
          >
            <MessageCircle className="w-5 h-5 stroke-[2] transform group-hover:scale-110 transition-transform" />
            <span>{postState.comments > 0 ? postState.comments : 'Comment'}</span>
          </button>
        </div>
      </div>

      <CommentModal
        isOpen={showCommentModal}
        onClose={() => setShowCommentModal(false)}
        post={postState}
        highlightedCommentId={highlightedCommentId}
        onCommentAdded={handleCommentAdded}
        onCommentDeleted={handleCommentDeleted}
      />

      <DeleteModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeletePost}
        title="Delete Post"
        message="Are you sure you want to delete this post? This action cannot be undone."
      />
    </article>
  );
}

const CommentItem = ({ comment, onReplyClick, highlightedCommentId, onProfileClick }: CommentItemProps) => {
  const { user } = useAuth();
  const [isHighlighted, setIsHighlighted] = useState(false);

  useEffect(() => {
    if (highlightedCommentId === comment.id) {
      setIsHighlighted(true);
    } else {
      setIsHighlighted(false);
    }
  }, [highlightedCommentId, comment.id]);

  return (
    <div className={`p-3 ${isHighlighted ? 'bg-gray-50' : ''}`}>
      <div className="flex items-center space-x-3">
        {comment.authorPhoto ? (
          <img
            src={comment.authorPhoto}
            alt={comment.authorName}
            className="h-8 w-8 min-w-[2rem] min-h-[2rem] flex-shrink-0 rounded-full object-cover"
          />
        ) : (
          <div className="avatar-32 h-8 w-8 min-w-[2rem] min-h-[2rem] flex-shrink-0 rounded-full bg-navy-700 flex items-center justify-center text-orange-500 font-semibold text-sm">
            {comment.authorName[0].toUpperCase()}
          </div>
        )}
        <div>
          <button
            onClick={() => onProfileClick(comment.authorId)}
            className="font-semibold text-black hover:underline capitalize"
          >
            {comment.authorName}
          </button>
          <p className="text-xs text-gray-600">
            {comment.createdAt?.seconds ? 
              formatDistanceToNow(new Date(comment.createdAt.seconds * 1000), { addSuffix: true }) :
              'Just now'
            }
          </p>
        </div>
      </div>
      <div className="mt-2 text-base text-gray-800">
        {comment.content}
      </div>
      <div className="mt-2 flex items-center space-x-4">
        <button
          onClick={() => onReplyClick(comment.id)}
          className="flex items-center space-x-2 text-gray-600 hover:text-gray-800"
        >
          <MessageCircle size={20} />
          <span className="text-sm">Reply</span>
        </button>
      </div>
    </div>
  );
};

const ReplyItem = ({ reply, onProfileClick }: ReplyItemProps) => {
  const { user } = useAuth();

  return (
    <div className="pl-12 p-3">
      <div className="flex items-center space-x-3">
        {reply.authorPhoto ? (
          <img
            src={reply.authorPhoto}
            alt={reply.authorName}
            className="h-8 w-8 min-w-[2rem] min-h-[2rem] flex-shrink-0 rounded-full object-cover"
          />
        ) : (
          <div className="avatar-32 h-8 w-8 min-w-[2rem] min-h-[2rem] flex-shrink-0 rounded-full bg-navy-700 flex items-center justify-center text-orange-500 font-semibold text-sm">
            {reply.authorName[0].toUpperCase()}
          </div>
        )}
        <div>
          <button
            onClick={() => onProfileClick(reply.authorId)}
            className="font-semibold text-black hover:underline capitalize"
          >
            {reply.authorName}
          </button>
          <p className="text-xs text-gray-600">
            {reply.createdAt?.seconds ? 
              formatDistanceToNow(new Date(reply.createdAt.seconds * 1000), { addSuffix: true }) :
              'Just now'
            }
          </p>
        </div>
      </div>
      <div className="mt-2 text-base text-gray-800">
        {reply.content}
      </div>
    </div>
  );
};